import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../../lib/api";
import { formatMoney, formatDate } from "../../lib/money";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Plus, Receipt, MessageSquare, ArrowRight } from "lucide-react";

function Tile({ label, value, testId }) {
  return (
    <Card className="border-slate-200" data-testid={testId}>
      <CardContent className="pt-6">
        <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl">{value}</p>
      </CardContent>
    </Card>
  );
}

export default function WorkerDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState({ weekCount: 0, weekTotalCents: 0, pendingCount: 0 });
  const [lookups, setLookups] = useState({ cards: {}, cats: {} });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [list, cards, cats] = await Promise.all([
        api.get("/expenses", { params: { page: 1, page_size: 10 } }),
        api.get("/cards/available"),
        api.get("/categories/active"),
      ]);
      setRecent(list.data.items);

      const since = new Date(); since.setDate(since.getDate() - 7);
      const weekItems = list.data.items.filter((e) => new Date(e.occurred_at) >= since);
      const weekTotal = weekItems.reduce((s, e) => s + (e.amount_cents || 0), 0);
      // for accurate pending count we ask the server for total count of pending
      const pending = await api.get("/expenses", { params: { page: 1, page_size: 1, status: "pending" } });
      setStats({ weekCount: weekItems.length, weekTotalCents: weekTotal, pendingCount: pending.data.total });

      setLookups({
        cards: Object.fromEntries(cards.data.map((c) => [c.id, c])),
        cats: Object.fromEntries(cats.data.map((c) => [c.id, c])),
      });
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const firstName = useMemo(() => (user?.name || "").split(" ")[0] || "there", [user]);

  return (
    <div data-testid="worker-dashboard">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Hi, {firstName}</h1>
          <p className="mt-1 text-sm text-slate-500">Log a swipe, edit pending ones, and review what's matched.</p>
        </div>
        <Link to="/expenses/new" data-testid="log-expense-cta">
          <Button size="lg" className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90 w-full sm:w-auto">
            <Plus className="mr-2 h-5 w-5" /> Log expense
          </Button>
        </Link>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4" data-testid="dashboard-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Tile label="This week" value={loading ? "—" : stats.weekCount} testId="tile-week-count" />
        <Tile label="Week total" value={loading ? "—" : formatMoney(stats.weekTotalCents)} testId="tile-week-total" />
        <Tile label="Pending" value={loading ? "—" : stats.pendingCount} testId="tile-pending-count" />
      </div>

      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-semibold text-slate-900">Recent expenses</CardTitle>
          <Link to="/expenses" data-testid="view-all-expenses-link" className="text-sm text-[#0F1E3D] hover:underline">
            View all <ArrowRight className="inline h-3 w-3" />
          </Link>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-6 text-center text-slate-500">Loading…</p>
          ) : recent.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 px-6 py-12 text-center">
              <Receipt className="mx-auto h-8 w-8 text-slate-400" />
              <p className="mt-3 text-sm font-medium text-slate-700">No expenses yet</p>
              <p className="text-xs text-slate-500">Tap "Log expense" to record your first swipe.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="recent-expenses-list">
              {recent.map((e) => (
                <li key={e.id} className="py-3" data-testid={`recent-expense-${e.id}`}>
                  <Link to={`/expenses/${e.id}`} className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-slate-900">{e.vendor}</span>
                        {e.comment && <MessageSquare className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                        {e.receipt_image_id && <Receipt className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-slate-500">
                        {formatDate(e.occurred_at)} ·{" "}
                        {e.card_label || lookups.cards[e.card_id]?.label || "Card"} ·{" "}
                        {e.category_name || lookups.cats[e.category_id]?.name || "Category"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">{formatMoney(e.amount_cents, e.currency)}</p>
                      <Badge
                        variant="secondary"
                        className={
                          e.status === "pending"
                            ? "bg-amber-50 text-amber-700"
                            : e.status === "matched"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }
                      >
                        {e.status}
                      </Badge>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
